/**
 * Whether this document is running inside a frame, and what to do about it.
 *
 * MSAL renews an expired token by opening a hidden iframe on the redirect URI.
 * That URI is `/auth/ms/callback`, and this service answers every unknown path
 * with `index.html`, so the console booted a second time inside that iframe:
 * same bundle, its own `/auth/sso/config`, its own `/auth/me`, and its own MSAL
 * instance calling `handleRedirectPromise`, which consumes the response hash
 * the parent window was waiting for. The parent then times out with
 * `BrowserAuthError: timed_out` and the person cannot sign in until they clear
 * the cookie by hand.
 *
 * Measured on dev rather than reasoned about: an iframe pointed at
 * `/auth/ms/callback` loads `index-BQeLqd2P.js`, renders the whole sign-in
 * screen, reports `window.top !== window.self`, and issues both auth calls.
 * That is the second boot, and it is what the three 401s in the report are.
 *
 * MSAL reads the response out of the iframe's own URL. It needs nothing from
 * the page loaded there, so the right page is an inert one.
 *
 * ONE THING FROM THE BACKEND CAN STOP ALL OF THIS. The frame below is opened
 * by this origin on this origin, so the response headers decide whether it
 * loads at all. `X-Frame-Options: SAMEORIGIN` and `frame-ancestors 'self'` in
 * `backend/src/routes/app.py` are what allow it. `DENY` and `'none'` read as
 * the safer values and make the browser refuse the frame, which puts the
 * sign-in timeout back silently: nothing here runs, so nothing here answers.
 * Measured in both directions, on both consoles.
 *
 * Written here as well as there because whoever adds a header does not
 * necessarily read a frontend module, and whoever edits this module should
 * know a header can switch it off. Two ends of one boundary.
 */

/** True when this document is not the top window. Wrapped in a try because a
 *  cross-origin parent makes the comparison throw, and a throw there means we
 *  are framed by somebody else, which is the case this guard exists for. */
export function isFramed(win: Window = window): boolean {
  try {
    return win.top !== win.self
  } catch {
    return true
  }
}

/**
 * What a framed document shows instead of the console.
 *
 * Not nothing. A blank frame is the same silence this codebase keeps finding:
 * whoever embedded it would see an empty box and no reason.
 */
export const FRAMED_NOTICE = {
  /** The sign-in library's own frame, mid-renewal. Nothing is wrong. */
  answering:
    'A sign-in is being completed. This frame is part of it, it has handed ' +
    'the result back, and it closes itself.',
  /** Somebody else's page, or the callback opened by hand. Different thing. */
  idle:
    'This console does not run inside a frame. Open it directly at its own ' +
    'address.',
} as const

/**
 * Render the notice and leave. Exported for the test, which is what makes this
 * a rule rather than a comment.
 *
 * Two sentences rather than one, taken from the TIM console. The page used to
 * say the same thing in both cases, and they are not the same thing: one is
 * the library working, the other is somebody framing the console. The person
 * who reads this is looking inside a frame at three in the morning, and which
 * of the two it is decides whether they keep looking.
 */
export function refuseToBoot(root: HTMLElement, outcome: FramedOutcome): void {
  root.textContent = FRAMED_NOTICE[outcome === 'sent' ? 'answering' : 'idle']
  root.setAttribute('data-framed', outcome)
}

export type FramedOutcome = 'sent' | 'nothing-to-send' 

/**
 * Hand the authentication response back to the window that asked for it.
 *
 * Not booting was only half. MSAL 5 does not read the response out of the
 * iframe's URL: the parent opens a `BroadcastChannel` named for the request
 * and waits for a message on it, and gives up with
 * `BrowserAuthError: timed_out` carrying the suffix `redirect_bridge_timeout`.
 * Verified in the installed 5.17.3, `dist/utils/BrowserUtils.mjs:176` and
 * `:205`, called from `dist/app/PublicClientApplication.mjs:85`.
 *
 * The page at the redirect URI is what sends that message, through the
 * library's own `./redirect-bridge` entry point. Nothing in this console
 * imported it, so the message was never sent and the parent timed out every
 * time, deterministically. A dedicated blank page would have had exactly the
 * same defect: the problem was never that the console booted, it was that
 * nobody answered.
 *
 * Imported dynamically so the ordinary boot does not carry it, and called only
 * where a response can be: the library throws when the URL holds no payload,
 * which is its own detection and better than a second copy of the parsing here.
 */
export async function answerTheParentWindow(): Promise<FramedOutcome> {
  const { broadcastResponseToMainFrame } = await import(
    '@azure/msal-browser/redirect-bridge'
  )
  try {
    await broadcastResponseToMainFrame()
    return 'sent'
  } catch {
    // No authentication payload in this URL. Somebody framed the console, or
    // opened the callback path by hand. Not an error worth a screen.
    return 'nothing-to-send'
  }
}


/**
 * The whole decision this file exists to make, in one place a test can drive.
 *
 * `main.tsx` used to hold the `if` itself, so a test could prove the pieces and
 * nothing could prove they were wired together. That is the shape this
 * programme has found six times: the piece is right and nobody calls it.
 */
export async function bootOrAnswer(options: {
  mount: HTMLElement
  boot: () => void
  framed?: () => boolean
  answer?: () => Promise<FramedOutcome | unknown>
}): Promise<void> {
  const framed = options.framed ?? (() => isFramed())
  if (!framed()) {
    options.boot()
    return
  }
  // Answer first, then say why nothing is here. The parent is not waiting for
  // this page to be quiet, it is waiting for a message.
  // Answer first, then say which of the two frames this is. The notice
  // depends on the outcome, so it cannot be written before there is one.
  const answer = options.answer ?? answerTheParentWindow
  const outcome = await answer()
  refuseToBoot(options.mount, outcome === 'sent' ? 'sent' : 'nothing-to-send')
}
